import { useId, type SVGProps } from 'react';

/**
 * Nadoutong brand mark — mono-on-dark variant.
 *
 * Eight radiating arrows around a double-ring hub. This console ships
 * the mark tinted for dark surfaces: the five "navy" arrows and the
 * outer ring stroke render with `currentColor` (drive from the parent
 * class, e.g. `text-on-surface`), while the top Claude-orange arrow,
 * the two sky-blue diagonal arrows, and the inner radial-gradient
 * disc keep their brand hues unchanged.
 *
 * Hex literals in this file are the single documented exception to
 * the project's "no hex in .tsx" rule — these are brand asset colors
 * that would be wrong to expose as tailwind tokens (they would
 * pollute the Kinetic Monolith palette).
 */

const ARROW_PATH = 'M 48 -7 H 72 V -14 L 94 0 L 72 14 V 7 H 48 Z';

const ARROW_PLACEMENTS: ReadonlyArray<{ readonly rot: number; readonly fill: string }> = [
  { rot: -90, fill: '#CC785C' }, // 12 o'clock — Claude orange
  { rot: -45, fill: '#6DA8D6' }, // 1–2 o'clock — sky
  { rot: 0, fill: 'currentColor' }, // 3 o'clock
  { rot: 45, fill: 'currentColor' }, // 4–5 o'clock
  { rot: 90, fill: 'currentColor' }, // 6 o'clock
  { rot: 135, fill: '#6DA8D6' }, // 7–8 o'clock — sky
  { rot: 180, fill: 'currentColor' }, // 9 o'clock
  { rot: 225, fill: 'currentColor' }, // 10–11 o'clock
];

export function BrandLogo(props: SVGProps<SVGSVGElement>): JSX.Element {
  const id = useId();
  const gradId = `ndt-grad-${id}`;
  return (
    <svg viewBox="0 0 200 200" aria-hidden="true" {...props}>
      <defs>
        <radialGradient id={gradId} cx="50%" cy="38%" r="70%">
          <stop offset="0%" stopColor="#8FC1E2" />
          <stop offset="100%" stopColor="#3E6890" />
        </radialGradient>
      </defs>
      <g transform="translate(100 100)">
        {ARROW_PLACEMENTS.map(({ rot, fill }) => (
          <path key={rot} d={ARROW_PATH} fill={fill} transform={`rotate(${rot})`} />
        ))}
      </g>
      <circle cx="100" cy="100" r="36" fill="none" stroke="currentColor" strokeWidth="8" />
      <circle cx="100" cy="100" r="28" fill={`url(#${gradId})`} />
    </svg>
  );
}
